import React, { useEffect, useMemo, useRef, useState } from 'react';
import { computeResize, type ResizeSide } from '../canvas/resizeRect';
import { useCanvasStore } from '../store/canvas-store';
import { framesById } from '../store/commands';
import { applyEffectPre } from '../effects/applyEffectPre';
import { applyEffectPost } from '../effects/applyEffectPost';
import { effectToCssFilter } from '../effects/css-filters';
import { createPaintSignal, frameNeedsContinuousPaint } from './paintScheduler';
import { visibleWorldRect, frameInView } from './culling';
import { useCanvasGestures } from '../canvas/useCanvasGestures';
import { snap } from '../canvas/guides';
import { useGuides } from '../canvas/GuideOverlay';
import { GuideOverlay } from '../canvas/GuideOverlay';
import { type Frame } from '../store/types';
import { isCanvasRenderable } from './canvasVisible';
import { useInteraction } from '../chrome/interaction-store';
import { useToolStore } from '../chrome/tool-store';
import { useCanvasGrid, gridLayers } from '../chrome/canvas-grid';
import { FrameContent } from './FrameContent';
import { AnchorEdges } from '../chrome/AnchorEdges';
import { CommentPins } from '../chrome/comments/CommentPins';
import { ChatChips } from '../chrome/ChatChips';
import { findDropTarget, shortId } from '../chrome/dropDetect';
import { resolveLayerAt } from '../chrome/layerSelect';
import { handleRecordPointerDown } from '../chrome/timeline/useRecordGesture';
import { useTimelineStore } from '../chrome/timeline/timeline-store';
import { invalidateCanvas, setCanvasInvalidator } from './canvasInvalidate';
import { useReadOnly, currentReadOnly } from '../chrome/readonly';

/**
 * Unified canvas-mode surface: ONE <canvas layoutsubtree> wrapping all frames
 * as direct children. Each frame-root contains BOTH chrome (titlebar +
 * resize handles) and content stacked vertically. Canvas paint loop draws
 * each frame-root at its world coords; returned DOMMatrix syncs the source
 * DOM transform so titlebar/handles/content stay clickable at painted pos.
 *
 * Pan/zoom handlers live on the canvas itself — empty canvas regions (no
 * frame painted there) receive pointer events; frame regions hit the
 * matrix-positioned DOM (titlebar/handles/inputs).
 *
 * Constraints from WICG html-in-canvas + html-in-canvas.dev demos:
 *  - NO devicePixelRatio scaling on canvas.width
 *  - ctx.reset() at top of every paint
 *  - try/catch around drawElementImage
 *  - source elements need transform-origin: 0 0; transition: none
 *  - children get grid-area: stack (forced position:static on direct children)
 */

/** Force the DOM-positioned path. Still honoured for back-compat, but DOM is now
 *  the DEFAULT (see USE_CANVAS_KEY) so this is rarely needed. Set to '1'. */
export const FORCE_DOM_KEY = 'codeframe.forceDom';

/** Opt INTO the painted `<canvas>` path. DOM is the default because
 *  `drawElementImage` rasterises at CSS px only — soft on Retina, unfixable
 *  in-browser today (WICG html-in-canvas #138). The canvas path keeps true
 *  WebGL/multipass effects but pays that blur, so it's opt-in for anyone who
 *  wants it. Set `localStorage[USE_CANVAS_KEY]='1'`. */
export const USE_CANVAS_KEY = 'codeframe.useCanvas';

/** Pure predicate over a localStorage-like reader (testable). '1' = force DOM. */
export function isForceDomValue(read: (k: string) => string | null): boolean {
  try {
    return read(FORCE_DOM_KEY) === '1';
  } catch {
    return false;
  }
}

/** Pure predicate: '1' = opt into the painted canvas path. */
export function isUseCanvasValue(read: (k: string) => string | null): boolean {
  try {
    return read(USE_CANVAS_KEY) === '1';
  } catch {
    return false;
  }
}

function forceDom(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return isForceDomValue((k) => localStorage.getItem(k));
}

function useCanvasOptIn(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return isUseCanvasValue((k) => localStorage.getItem(k));
}

interface CanvasWithPaint extends HTMLCanvasElement {
  onpaint: ((ev: { changedElements?: Element[] }) => void) | null;
  requestPaint: () => void;
}
interface Ctx2DWithDrawElement extends CanvasRenderingContext2D {
  drawElementImage?: (el: Element, x: number, y: number) => DOMMatrix;
}

export function CanvasSurface() {
  const frames = useCanvasStore((s) => s.doc.frames);
  const viewport = useCanvasStore((s) => s.doc.viewport);
  const annotationsVisible = useCanvasStore((s) => s.annotationsVisible);
  const panCursor = useToolStore((s) => s.tool === 'hand' || s.spaceHeld);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  // Let frame scripts request repaints (ctx.invalidate → canvas.requestPaint).
  useEffect(() => {
    setCanvasInvalidator(() => (canvasRef.current as CanvasWithPaint | null)?.requestPaint?.());
    return () => setCanvasInvalidator(null);
  }, []);

  const sortedFrames = useMemo(() => [...frames].sort((a, b) => a.z - b.z), [frames]);
  // byId keeps ALL frames so instance resolution sees every Main regardless of visibility
  const byId = useMemo(() => framesById(frames), [frames]);
  // visibleFrames excludes note frames when annotations are hidden, and
  // panel-owned tool frames (they live only in the docked Editor Panel).
  const visibleFrames = useMemo(
    () => sortedFrames.filter((f) => isCanvasRenderable(f, annotationsVisible)),
    [sortedFrames, annotationsVisible],
  );

  // DOM-positioned divs are the DEFAULT render path: they render at native
  // device-pixel-ratio, so text/vectors stay crisp on Retina. The painted
  // `<canvas>` path (drawElementImage) rasterises at CSS px only — visibly soft
  // on high-DPI and unfixable in-browser today (WICG html-in-canvas #138) — so
  // it is OPT-IN via `localStorage['codeframe.useCanvas']='1'` for anyone who
  // wants its true WebGL/multipass effects and accepts the blur. `forceDom`
  // still hard-forces DOM for back-compat and wins over the opt-in.
  const hasSupport = useMemo(() => {
    if (typeof CanvasRenderingContext2D === 'undefined') return false;
    if (forceDom()) return false;
    if (!useCanvasOptIn()) return false; // DOM is the default
    return 'drawElementImage' in CanvasRenderingContext2D.prototype;
  }, []);

  // ---- Paint loop ----
  useEffect(() => {
    if (!hasSupport) return; // fallback path renders DOM directly
    const canvas = canvasRef.current as CanvasWithPaint | null;
    if (!canvas) return;
    const ctx = canvas.getContext('2d') as Ctx2DWithDrawElement | null;
    if (!ctx || typeof ctx.drawElementImage !== 'function' || typeof canvas.requestPaint !== 'function') {
      return;
    }

    const signal = createPaintSignal({ eventTarget: canvas });

    canvas.onpaint = () => {
      const state = useCanvasStore.getState();
      const vp = state.doc.viewport;
      const annVisible = state.annotationsVisible;
      const ordered = [...state.doc.frames]
        .filter((f) => isCanvasRenderable(f, annVisible))
        .sort((a, b) => a.z - b.z);
      ctx.reset();
      ctx.translate(vp.pan.x, vp.pan.y);
      ctx.scale(vp.zoom, vp.zoom);
      // Cull frames outside the visible viewport (256px screen-space pre-render
      // margin). Offscreen frames get content-visibility:hidden so the browser
      // skips their layout/style/paint too — not just the canvas draw. All
      // frame roots share grid-area:stack at the canvas origin (repositioned
      // only by the draw matrix), so content-visibility:auto can't self-skip;
      // we toggle it imperatively from the cull decision instead.
      const view = visibleWorldRect(canvas.width, canvas.height, vp, 256);
      let anyLive = false;
      for (const f of ordered) {
        const el = rootsRef.current.get(f.id);
        if (!el) continue;
        const fh = f.h ?? ((el as HTMLElement).offsetHeight || f.w);
        const inView = frameInView(f.x, f.y, f.w, fh, view);
        const cv = inView ? '' : 'hidden';
        if (el.style.contentVisibility !== cv) el.style.contentVisibility = cv;
        if (!inView) continue;
        ctx.save();
        ctx.translate(f.x, f.y);
        applyEffectPre(ctx, f.effect);
        try {
          const m = ctx.drawElementImage!(el, 0, 0);
          if (m) el.style.transform = m.toString();
        } catch {
          /* "No cached paint record" — next tick recovers */
          anyLive = true; // retry next tick until the paint record exists
        }
        try {
          applyEffectPost(ctx, f.effect, el, { w: f.w, h: f.h });
        } catch {
          /* effect-post must never crash the unified paint loop */
        }
        if (frameNeedsContinuousPaint(f, el)) anyLive = true;
        ctx.restore();
      }
      signal.setLive(anyLive);
    };

    const ro = new ResizeObserver(([entry]) => {
      canvas.width = Math.round(entry.contentRect.width);
      canvas.height = Math.round(entry.contentRect.height);
      signal.markDirty();
    });
    ro.observe(canvas);

    let raf = 0;
    const tick = () => {
      if (signal.shouldPaint()) {
        canvas.requestPaint?.();
        signal.afterPaint();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      canvas.onpaint = null;
      ro.disconnect();
      signal.dispose();
      for (const el of rootsRef.current.values()) {
        el.style.transform = '';
        el.style.contentVisibility = '';
      }
    };
  }, [hasSupport]);

  // ---- Pan / zoom gestures on outer wrapper (works for canvas + fallback) ----
  const wrapperRef = useRef<HTMLDivElement>(null);
  useCanvasGestures(wrapperRef);

  // Switchable pan/zoom-synced grid (canvas-grid recipe; default 'line').
  const gridKind = useCanvasGrid((s) => s.kind);
  const gridSize = 32 * viewport.zoom;
  const gridX = viewport.pan.x % gridSize;
  const gridY = viewport.pan.y % gridSize;

  return (
    <div
      ref={wrapperRef}
      className={`absolute inset-0 overflow-hidden select-none ${panCursor ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
      style={gridLayers(gridKind, gridSize, gridX, gridY)}
    >
      {hasSupport ? (
        <canvas
          ref={canvasRef}
          // @ts-expect-error layoutsubtree is experimental
          layoutsubtree=""
          role="application"
          aria-label="Design canvas"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateAreas: '"stack"',
            cursor: 'grab',
          }}
        >
          {visibleFrames.map((f) => (
            <UnifiedFrameRoot
              key={f.id}
              frame={f}
              rootsRef={rootsRef}
              byId={byId}
            />
          ))}
        </canvas>
      ) : (
        <UnifiedFallback frames={visibleFrames} byId={byId} viewport={viewport} />
      )}
      {/* World-transformed overlay layer for AnchorEdges + GuideOverlay.
          pointer-events:none so it never intercepts canvas/frame clicks. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom})`,
          transformOrigin: '0 0',
          pointerEvents: 'none',
        }}
      >
        <AnchorEdges />
        <CommentPins />
        <ChatChips />
      </div>
      <GuideOverlay />
      {/* DOM is the default render path now (crisp on Retina) — no chrome for it.
          Only warn when someone opted INTO the canvas path but the browser can't
          provide drawElementImage, so they understand why it didn't engage. */}
      {!hasSupport && useCanvasOptIn() && !forceDom() && (
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 z-50 bg-amber-100 border border-amber-300 text-amber-900 text-xs font-mono px-3 py-1 rounded shadow"
          title={`Painted-canvas path requested via localStorage['${USE_CANVAS_KEY}'] but drawElementImage is unavailable — enable chrome://flags/#canvas-draw-element on Chrome Canary 147+.`}
        >
          Canvas path unavailable — using DOM
        </div>
      )}
    </div>
  );
}

/** Fallback when drawElementImage absent: render frames as absolute-positioned
 *  divs inside a world-transformed container. Same React tree as the canvas
 *  path — only the wrapper differs. */
function UnifiedFallback({
  frames,
  byId,
  viewport,
}: {
  frames: Frame[];
  byId: Map<string, Frame>;
  viewport: { pan: { x: number; y: number }; zoom: number };
}) {
  const dummyRoots = useRef<Map<string, HTMLDivElement>>(new Map());
  // Pan/zoom handlers: attach to the world container's parent (the background div above)
  // — but we don't have a ref to it here. For simplicity in fallback, reuse
  // wheel-on-document; pan via Marquee/title-drag still works.
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom})`,
        transformOrigin: '0 0',
        pointerEvents: 'none',
      }}
    >
      {frames.map((f) => (
        <div
          key={f.id}
          style={{
            position: 'absolute',
            left: f.x,
            top: f.y,
            pointerEvents: 'auto',
            // World-positioned here, so the browser can self-skip offscreen
            // frames. Intrinsic size avoids scroll/layout jump when skipped.
            contentVisibility: 'auto',
            containIntrinsicSize: `${f.w}px ${f.h ?? 200}px`,
          }}
        >
          <UnifiedFrameRoot
            frame={f}
            rootsRef={dummyRoots}
            byId={byId}
            domEffects
          />
        </div>
      ))}
    </div>
  );
}

// ---- per-frame root (chrome + content + handles) ----

interface UnifiedFrameRootProps {
  frame: Frame;
  rootsRef: React.MutableRefObject<Map<string, HTMLDivElement>>;
  byId: Map<string, Frame>;
  /** DOM path applies effects via CSS filter (the canvas path paints them in
   *  the draw loop instead, so it passes false to avoid double-applying). */
  domEffects?: boolean;
}

function UnifiedFrameRoot({ frame, rootsRef, byId, domEffects = false }: UnifiedFrameRootProps) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const zoom = useCanvasStore((s) => s.doc.viewport.zoom);
  const selected = useCanvasStore((s) => s.selection.includes(frame.id));
  const selectionLen = useCanvasStore((s) => s.selection.length);
  const hovered = useInteraction((s) => s.hoveredId === frame.id);
  const readOnly = useReadOnly();
  const localRef = useRef<HTMLDivElement | null>(null);

  // Click-anywhere select + drag-to-move. Bound to the whole frame root: design
  // frames select from the body (until selected, then their live HTML stays
  // interactive); tool/note frames select only via the titlebar.
  useEffect(() => {
    const node = localRef.current;
    if (!node) return;
    const designKind = frame.kind === 'free' || frame.kind === 'main' || frame.kind === 'instance' || frame.kind === 'tsxFree' || frame.kind === 'tsxMain' || frame.kind === 'tsxInstance';
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (e: MouseEvent) => {
      // Record mode: when the timeline dock is open and a single design frame
      // is selected (= useMotionScope's scope), dragging a node keyframes its
      // x/y at the playhead. Claims the gesture before layer-select/frame-drag.
      const ts = useTimelineStore.getState();
      const sel = useCanvasStore.getState().selection;
      // Only animatable frame kinds (matching DESIGN_KINDS / useMotionScope's
      // canAnimate) can record — otherwise motion.init no-ops and the commit
      // would crash. The scope MUST be undefined for non-animatable frames.
      const ANIMATABLE = new Set<Frame['kind']>(['free', 'main', 'tsxFree', 'tsxMain']);
      const scoped = ts.open && sel.length === 1 ? sel[0] : undefined;
      const scope = scoped && ANIMATABLE.has(frame.kind) ? scoped : undefined;
      if (handleRecordPointerDown((e as unknown as { nativeEvent?: PointerEvent }).nativeEvent ?? (e as unknown as PointerEvent), frame.id, scope)) return;
      if (e.altKey) {
        const ref = resolveLayerAt(e.clientX, e.clientY, 'unified');
        if (ref) {
          e.preventDefault();
          e.stopPropagation();
          useCanvasStore.getState().setLayerSelection(ref);
          return;
        }
      }
      const target = e.target as HTMLElement;
      if (target.closest('[data-resize-handle]')) return;
      const onTitlebar = !!target.closest('[data-titlebar]');
      const cur = useCanvasStore.getState().selection;
      const isSelected = cur.includes(frame.id);
      if (!onTitlebar) {
        if (!designKind) return;
        if (isSelected && !e.shiftKey) return;
        e.preventDefault();
      }
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      e.stopPropagation();
      if (e.shiftKey) {
        const next = cur.includes(frame.id) ? cur.filter((id) => id !== frame.id) : [...cur, frame.id];
        dispatch({ t: 'select', ids: next });
      } else if (!isSelected) {
        dispatch({ t: 'select', ids: [frame.id] });
      }
    };
    const onMove = (e: MouseEvent) => {
      if (!dragging) return;
      if (currentReadOnly()) return; // viewer: select to inspect, but never move
      const dx = (e.clientX - lastX) / zoom;
      const dy = (e.clientY - lastY) / zoom;
      lastX = e.clientX;
      lastY = e.clientY;
      const state = useCanvasStore.getState();
      const ids = state.selection.includes(frame.id) ? state.selection : [frame.id];
      if (ids.length === 1) {
        const liveFrame = state.doc.frames.find((f) => f.id === frame.id);
        if (liveFrame) {
          const others = state.doc.frames.filter((f) => f.id !== frame.id);
          const proposed = { ...liveFrame, x: liveFrame.x + dx, y: liveFrame.y + dy };
          const r = snap(proposed, others, 6 / zoom);
          const sdx = r.x - liveFrame.x;
          const sdy = r.y - liveFrame.y;
          useGuides.getState().set(r.guides.vertical, r.guides.horizontal);
          dispatch({ t: 'frame.move', ids, dx: sdx, dy: sdy });
          return;
        }
      }
      useGuides.getState().clear();
      dispatch({ t: 'frame.move', ids, dx, dy });
    };
    const onUp = (e: MouseEvent) => {
      if (!dragging) return;
      dragging = false;
      useGuides.getState().clear();
      if (currentReadOnly()) return; // no drag-to-promote for viewers
      const store = useCanvasStore.getState();
      const targetId = findDropTarget(e.clientX, e.clientY, frame.id);
      if (!targetId) return;
      const source = store.doc.frames.find((f) => f.id === frame.id);
      const target = store.doc.frames.find((f) => f.id === targetId);
      if (!source || !target) return;
      // tsxFree path: promote + create tsx instance.
      if (source.kind === 'tsxFree' || source.kind === 'tsxMain') {
        store.beginTransaction('drag-to-promote');
        try {
          const mainId = source.id;
          if (source.kind === 'tsxFree') {
            dispatch({ t: 'tsxFrame.promoteToMain', id: source.id });
          }
          dispatch({
            t: 'tsxInstance.create',
            id: shortId(),
            mainId,
            x: target.x + 20,
            y: target.y + 20,
            propValues: {},
          });
        } finally {
          store.endTransaction();
        }
        return;
      }
      if (source.kind !== 'free' && source.kind !== 'main') return;
      if (target.kind !== 'free' && target.kind !== 'main' && target.kind !== 'instance') return;
      store.beginTransaction('drag-to-promote');
      try {
        let mainId = source.id;
        if (source.kind === 'free') {
          dispatch({ t: 'frame.promoteToMain', id: source.id });
          mainId = source.id;
        }
        dispatch({
          t: 'instance.create',
          id: shortId(),
          mainId,
          x: target.x + 20,
          y: target.y + 20,
          slotValues: {},
        });
      } finally {
        store.endTransaction();
      }
    };
    node.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      node.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dispatch, frame.id, frame.kind, zoom]);

  const kindBadge = (frame.kind === 'main' || frame.kind === 'tsxMain') ? '★' : (frame.kind === 'instance' || frame.kind === 'tsxInstance') ? '↳' : '';
  const kindColor = (frame.kind === 'main' || frame.kind === 'tsxMain' || frame.kind === 'instance' || frame.kind === 'tsxInstance') ? 'var(--color-main)' : 'var(--color-text-muted)';

  return (
    <div
      ref={(el) => {
        localRef.current = el;
        if (el) rootsRef.current.set(frame.id, el);
        else rootsRef.current.delete(frame.id);
      }}
      data-frame-id={frame.id}
      onMouseMove={(e) => {
        if (e.buttons !== 0) return; // dragging / panning
        if (useInteraction.getState().panZoomActive) return;
        useInteraction.getState().setHovered(frame.id);
      }}
      onMouseLeave={() => {
        if (useInteraction.getState().hoveredId === frame.id) useInteraction.getState().setHovered(null);
      }}
      style={{
        gridArea: 'stack',
        width: frame.w,
        height: frame.h,
        transformOrigin: '0 0',
        transition: 'none',
        background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-1)',
        borderRadius: 6,
        outline: selected
          ? '2px solid var(--color-accent)'
          : hovered
            ? '2px solid var(--color-accent)'
            : (frame.kind === 'main' || frame.kind === 'tsxMain')
              ? '1px solid var(--color-main)'
              : '1px solid var(--color-border)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        data-titlebar
        style={{
          padding: '4px 8px',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          cursor: 'move',
          background: 'var(--color-titlebar-bg)',
          borderBottom: '1px solid var(--color-titlebar-border)',
          userSelect: 'none',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {kindBadge && <span style={{ color: kindColor, marginRight: 4 }}>{kindBadge}</span>}
        {frame.title}
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          filter:
            domEffects && frame.effect.kind !== 'none'
              ? effectToCssFilter(frame.effect)
              : undefined,
        }}
      >
        <FrameContent frame={frame} byId={byId} onInvalidate={invalidateCanvas} />
      </div>
      {selected && !readOnly && selectionLen === 1 && <UnifiedResizeHandles frame={frame} />}
    </div>
  );
}

/** Unified-path resize handles — parity with the legacy Frame.tsx path: pure
 *  computeResize + extended {x,y,w,h} command (B4), constant SCREEN-size handles,
 *  edge-grab strips, live W×H HUD, transactional, Shift=ratio / Alt=center. */
function UnifiedResizeHandles({ frame }: { frame: Frame }) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const zoom = useCanvasStore((s) => s.doc.viewport.zoom);
  const beginTxn = useCanvasStore((s) => s.beginTransaction);
  const endTxn = useCanvasStore((s) => s.endTransaction);
  const [hud, setHud] = useState<{ w: number; h: number } | null>(null);

  const start = (side: ResizeSide) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const rect = { x: frame.x, y: frame.y, w: frame.w, h: frame.h ?? 200 };
    beginTxn('resize');
    const onMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / zoom;
      const dy = (ev.clientY - startY) / zoom;
      const r = computeResize(rect, side, dx, dy, { ratio: ev.shiftKey, center: ev.altKey }, []);
      dispatch({ t: 'frame.resize', id: frame.id, x: r.x, y: r.y, w: r.w, h: r.h });
      setHud({ w: r.w, h: r.h });
    };
    const onUp = () => {
      endTxn();
      setHud(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const s = 1 / zoom;
  const sq = 11 * s, off = -sq / 2, strip = 16 * s;
  const corner: React.CSSProperties = {
    position: 'absolute', width: sq, height: sq,
    background: 'var(--color-surface)', border: `${1.5 * s}px solid var(--color-accent)`,
    borderRadius: 2 * s, zIndex: 6, animation: 'cf-hd-in .24s var(--ease-spring) both',
  };
  const edge: React.CSSProperties = { position: 'absolute', zIndex: 4 };
  const hp = { 'data-resize-handle': true } as const;
  return (
    <>
      <div {...hp} onMouseDown={start('t')} style={{ ...edge, top: off, left: 0, right: 0, height: strip, cursor: 'ns-resize' }} />
      <div {...hp} onMouseDown={start('b')} style={{ ...edge, bottom: off, left: 0, right: 0, height: strip, cursor: 'ns-resize' }} />
      <div {...hp} onMouseDown={start('l')} style={{ ...edge, left: off, top: 0, bottom: 0, width: strip, cursor: 'ew-resize' }} />
      <div {...hp} onMouseDown={start('r')} style={{ ...edge, right: off, top: 0, bottom: 0, width: strip, cursor: 'ew-resize' }} />
      <div {...hp} onMouseDown={start('tl')} style={{ ...corner, left: off, top: off, cursor: 'nwse-resize' }} />
      <div {...hp} onMouseDown={start('tr')} style={{ ...corner, right: off, top: off, cursor: 'nesw-resize' }} />
      <div {...hp} onMouseDown={start('bl')} style={{ ...corner, left: off, bottom: off, cursor: 'nesw-resize' }} />
      <div {...hp} onMouseDown={start('br')} style={{ ...corner, right: off, bottom: off, cursor: 'nwse-resize' }} />
      {hud && (
        <div aria-hidden style={{
          position: 'absolute', right: 0, bottom: -26 * s, zIndex: 9,
          transform: `scale(${s})`, transformOrigin: 'bottom right',
          background: 'var(--color-accent)', color: '#fff', fontFamily: 'var(--font-mono)',
          fontSize: 12, padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap', pointerEvents: 'none',
        }}>
          {hud.w} × {hud.h}
        </div>
      )}
    </>
  );
}
